import type { EmbeddedCheckout, ExpectedPurchase } from "../data/checkout";
import { normalizePaymentOrder } from "../data/checkout";
import type { Order } from "../types";

export const stripeScriptUrl = "https://js.stripe.com/basil/stripe.js";
export type StripeEmbeddedInstance = { mount: (element: HTMLElement) => void; destroy: () => void };
export type StripeFactory = (key: string, options: { locale: "it" }) => {
  initEmbeddedCheckout: (options: { fetchClientSecret: () => Promise<string>; onComplete: () => void }) => Promise<StripeEmbeddedInstance>;
};
declare global { interface Window { Stripe?: StripeFactory } }
const abortError = () => new DOMException("Operazione annullata", "AbortError");
export const isPaymentAbort = (error: unknown) => error instanceof Error && error.name === "AbortError";

export function abortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const cancel = () => reject(abortError());
    signal.addEventListener("abort", cancel, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", cancel));
  });
}

/** A single script request is shared, while cancelling one caller never cancels another. */
export function createStripeLoader(environment: {
  factory: () => StripeFactory | undefined;
  append: (loaded: () => void, failed: () => void) => () => void;
}, timeoutMs = 20000) {
  let pending: Promise<StripeFactory> | undefined;
  return (signal?: AbortSignal): Promise<StripeFactory> => {
    if (signal?.aborted) return Promise.reject(abortError());
    const available = environment.factory();
    if (available) return Promise.resolve(available);
    if (!pending) {
      pending = new Promise<StripeFactory>((resolve, reject) => {
        let settled = false; let cleanup = () => {};
        const finish = (failed = false) => {
          if (settled) return; settled = true; clearTimeout(timer); cleanup();
          const factory = environment.factory();
          if (failed || !factory) reject(new Error("Il modulo sicuro non si è caricato. Riprova.")); else resolve(factory);
        };
        const timer = setTimeout(() => finish(true), timeoutMs);
        try { cleanup = environment.append(() => finish(), () => finish(true)); } catch { finish(true); }
      }).catch((error: unknown) => { pending = undefined; throw error; });
    }
    return abortable(pending, signal);
  };
}

const loadStripe = createStripeLoader({
  factory: () => window.Stripe,
  append(loaded, failed) {
    const script = document.createElement("script"); script.src = stripeScriptUrl; script.async = true;
    const onError = () => { script.remove(); failed(); };
    script.addEventListener("load", loaded, { once: true }); script.addEventListener("error", onError, { once: true });
    document.head.appendChild(script);
    return () => {
      script.removeEventListener("load", loaded); script.removeEventListener("error", onError);
      if (!window.Stripe) script.remove();
    };
  },
});

let initializationQueue: Promise<unknown> = Promise.resolve();
/** Serialized initialization avoids late SDK results overlapping React StrictMode cleanup. */
export function mountEmbeddedPayment(options: {
  checkout: EmbeddedCheckout; element: HTMLElement; signal: AbortSignal;
  onReady: () => void; onComplete: () => void;
  loader?: (signal?: AbortSignal) => Promise<StripeFactory>;
}): Promise<void> {
  let instance: StripeEmbeddedInstance | undefined; let destroyed = false; let completed = false;
  const destroy = () => {
    if (!instance || destroyed) return; destroyed = true;
    try { instance.destroy(); } catch { /* Cleanup cannot restore a payment or create another order. */ }
  };
  options.signal.addEventListener("abort", destroy, { once: true });
  const run = initializationQueue.catch(() => undefined).then(async () => {
    if (options.signal.aborted) throw abortError();
    const factory = await (options.loader || loadStripe)(options.signal);
    if (options.signal.aborted) throw abortError();
    const stripe = factory(options.checkout.publishableKey, { locale: "it" });
    if (typeof stripe?.initEmbeddedCheckout !== "function") throw new Error("Il modulo sicuro non è compatibile. Riprova.");
    instance = await stripe.initEmbeddedCheckout({
      fetchClientSecret: async () => {
        if (options.signal.aborted) throw abortError();
        return options.checkout.clientSecret;
      },
      onComplete() {
        if (options.signal.aborted || completed) return;
        completed = true; destroy(); options.onComplete();
      },
    });
    if (options.signal.aborted || completed) { destroy(); if (options.signal.aborted) throw abortError(); return; }
    try { instance.mount(options.element); options.onReady(); }
    catch (error) { destroy(); throw error; }
  }).catch((error: unknown) => { destroy(); throw error; });
  initializationQueue = run.catch(() => undefined);
  void run.finally(() => { if (options.signal.aborted) options.signal.removeEventListener("abort", destroy); }).catch(() => undefined);
  return abortable(run, options.signal);
}

export function paymentDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); reject(abortError()); };
    const timer = setTimeout(() => { signal.removeEventListener("abort", cancel); resolve(); }, milliseconds);
    signal.addEventListener("abort", cancel, { once: true });
  });
}

/** Read-only polling: completion in Stripe's frame is not permission to mark an order paid. */
export async function verifyPaymentOrder(options: {
  expected: ExpectedPurchase & { orderId: string }; signal: AbortSignal;
  read: (id: string, signal: AbortSignal) => Promise<unknown>;
  onOrder: (order: Order) => void; attempts?: number; delayMs?: number;
}): Promise<Order> {
  const attempts = Math.min(10, Math.max(1, options.attempts ?? 8)); let latest: Order | undefined;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (options.signal.aborted) throw abortError();
    const response = await abortable(options.read(options.expected.orderId, options.signal), options.signal);
    if (options.signal.aborted) throw abortError();
    const envelope = response !== null && typeof response === "object" ? response as { order?: unknown } : {};
    latest = normalizePaymentOrder(envelope.order, options.expected); options.onOrder(latest);
    if (latest.status !== "awaiting_payment") return latest;
    if (attempt < attempts - 1) await paymentDelay(options.delayMs ?? 2500, options.signal);
  }
  return latest!;
}

export function expectedPaymentOrder(order: Order): ExpectedPurchase & { orderId: string } {
  return { orderId: order.id, buyerId: order.buyerId, sellerId: order.sellerId, listingId: order.listingId, totalCents: Math.round(order.totalEur * 100) };
}
